import React, { useEffect, useMemo, useState } from 'react';

import {
  ActionIcon,
  Button,
  createStyles,
  Divider,
  Group,
  LoadingOverlay,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useMediaQuery, useShallowEffect } from '@mantine/hooks';
import { IconExclamationCircle, IconTag, IconTrash } from '@tabler/icons-react';
import { AxiosError, AxiosResponse } from 'axios';
import AvailableDiscounts from 'components/Discounts/AvailableDiscounts';
import PaymentMethodSelect from 'components/form-fields/auto-complete-fields/PaymentMethodSelect';
import { getInvoiceActionEnabledMap } from 'components/invoices/utils';
import KiloAlert from 'components/KiloAlert';
import MoneyField from 'components/MoneyField';
import TaxRatePercentageInput from 'components/TaxRatePercentageInput';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { UseMutationResult, UseQueryResult } from 'react-query';
import {
  DiscountList,
  ErrorMessage,
  Invoice,
  InvoiceRetrieve,
  InvoiceUpdate,
  InvoiceUpdatePaymentDate,
  InvoiceUpdateResponse,
} from 'types';
import { NonStripePaymentMethod } from 'types/enums';
import { formatDateWithOrdinal, parseDateTimeToDate } from 'utils/date-functions';
import { formatDiscountValue, getDiscountFormValue } from 'utils/discount.helpers';
import { getPaymentMethodName } from 'utils/formatters';
import notificationHandler from 'utils/notifications';

import { ApplyScopeField, invoiceUpdateSuccessMessage } from './applyScopeMessages';
import ApplyScopeModal, { ApplyScope } from './ApplyScopeModal';
import InvoiceSummary from './InvoiceSummary';
import { InvoiceDrawerState } from '../types';

const useStyles = createStyles(theme => ({
  pm: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.xl,
    [theme.fn.smallerThan('sm')]: {
      display: 'block',
    },
  },
  invoices: {
    display: 'grid',
    gridTemplateColumns: '3fr 3fr 2fr 2fr auto',
    gap: theme.spacing.xs,
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: '3fr 3fr 2fr 2fr auto',
      gap: theme.spacing.xs,
      wordBreak: 'break-word',
    },
  },
}));

export interface InvoiceItem {
  invoiceItemId: string;
  productName: string;
  productPrice: string;
  productId: string;
  itemPrice: number;
  quantity: number;
  taxRateValue: number;
  productType: string;
  createdAt: string;
  discountAmount: number | undefined;
  discount: string | null;
  discountValue: number | undefined;
  discountType: 'PERCENTAGE' | 'FIXED_AMOUNT' | undefined;
  discountName: string | undefined;
}

interface FormValues {
  invoiceId: string;
  dueDate: Date;
  paymentDate: Date | null;
  autoPay: 'True' | 'False';
  paymentMethod: string | null;
  nonStripePaymentMethod: 'Cash or Check' | 'Gift Card' | 'E Transfer' | 'POS Terminal' | null;
  invoiceItems: InvoiceItem[];
}

type InvoiceInfoTabProps = {
  state: InvoiceDrawerState;
  setState: React.Dispatch<React.SetStateAction<InvoiceDrawerState>>;
  invoiceId: string;
  invoiceQuery: UseQueryResult<{
    data: InvoiceRetrieve;
  }>;
  updateInvoiceMutation: UseMutationResult<
    AxiosResponse<InvoiceUpdateResponse>,
    AxiosError<ErrorMessage>,
    InvoiceUpdate
  >;
  updateInvoicePaymentDateMutation: UseMutationResult<
    AxiosResponse<Invoice>,
    AxiosError<ErrorMessage>,
    InvoiceUpdatePaymentDate
  >;
  // Save is enabled only once the date actually moved, and the button lives in the drawer footer,
  // so the comparison is reported upward rather than duplicated there.
  setIsPaymentDateDirty: React.Dispatch<React.SetStateAction<boolean>>;
};

const RETAIL_FEES_TYPES = ['RETAIL', 'FEES AND MISCELLANEOUS'] as const;
const PACKAGE_TYPE = 'PACKAGE';

const InvoiceInfoTab = ({
  state,
  setState,
  invoiceId,
  invoiceQuery,
  updateInvoiceMutation,
  updateInvoicePaymentDateMutation,
  setIsPaymentDateDirty,
}: InvoiceInfoTabProps): JSX.Element => {
  const { classes, theme } = useStyles();
  const { country, copy, formatCurrency } = useLocalization();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const { errorNotification, successNotification } = notificationHandler();
  const { hasPerm, session } = useAuth();
  const { trackInvoiceApplyScopeSelected, trackInvoicePaymentDateEdited } = useStaffTracking();
  // Applying an edit to the package writes athlete packages across the chain, which is the
  // package screen's permission and a separate checkbox from Manage Invoices. The endpoint
  // refuses the flag without it, so offering the option would be offering a failed save.
  const canMutateAthletePackages = hasPerm('AthletePackageMutate');

  const [isEditEnabled, setIsEditEnabled] = useState<boolean>(false);
  // The edit the staff member submitted, held back while the prompt asks how far it should reach.
  const [pendingEdit, setPendingEdit] = useState<{
    payload: InvoiceUpdate;
    changedFields: ApplyScopeField[];
  } | null>(null);

  const isDisabled = state !== 'Edit Invoice';
  // The Payment Date picker is the one input that stays live in 'Edit Payment Date', and it is
  // locked in every other state — including 'Edit Invoice', where a paid invoice never lands.
  const isPaymentDateDisabled = state !== 'Edit Payment Date';

  const form = useForm<FormValues>({
    initialValues: {
      invoiceId: invoiceId,
      dueDate: new Date(),
      paymentDate: null,
      autoPay: 'False',
      paymentMethod: null,
      nonStripePaymentMethod: null,
      invoiceItems: [],
    },

    validate: {
      autoPay: (value, formValues) =>
        value === 'True' && formValues.nonStripePaymentMethod !== null
          ? 'Autppay cannot be on when payment method is cash or check'
          : null,
      invoiceItems: {
        itemPrice: value => (value >= 0 ? null : 'Price must be greater than or equal to $0.00'),
        quantity: value => (value >= 1 ? null : 'Quantity must be greater than or equal to 1'),
        taxRateValue: value => (value >= 0 ? null : 'Tax must be greater than or equal to 0%'),
      },
    },
  });

  const isPackageItem = (item: InvoiceItem): boolean => {
    return item?.productType === PACKAGE_TYPE;
  };

  const isRetailOrFeesItem = (item: InvoiceItem): boolean => {
    return RETAIL_FEES_TYPES.includes(item?.productType as any);
  };

  const canDeleteItem = (item: InvoiceItem): boolean => {
    return isRetailOrFeesItem(item);
  };

  const sortByCreatedDate = (a: InvoiceItem, b: InvoiceItem): number => {
    return dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf();
  };

  const groupedInvoiceItems = useMemo(() => {
    const items = form.values.invoiceItems;
    const packageItems = items.filter(isPackageItem);
    const retailFeesItems = items.filter(isRetailOrFeesItem).sort(sortByCreatedDate);
    const hasBothTypes = packageItems.length > 0 && retailFeesItems.length > 0;

    return {
      packageItems,
      retailFeesItems,
      hasBothTypes,
    };
  }, [form.values.invoiceItems]);

  const handlePaymentChange = (value: string): void => {
    form.setFieldValue('paymentMethod', value);
    if (Object.values(NonStripePaymentMethod).includes(value as NonStripePaymentMethod)) {
      form.setFieldValue('nonStripePaymentMethod', value as FormValues['nonStripePaymentMethod']);
      form.setFieldValue('autoPay', 'False');
    } else {
      form.setFieldValue('nonStripePaymentMethod', null);
    }
  };

  const handleDeleteItem = (index: number): void => {
    const item = form.values.invoiceItems[index];
    if (!item) {
      errorNotification('Delete failed', 'Item not found');
      return;
    }
    const remainingItems = form.values.invoiceItems.filter((_, i) => i !== index);
    if (remainingItems.length === 0) {
      errorNotification('Delete failed', 'Invoice must have at least 1 item');
      return;
    }
    form.removeListItem('invoiceItems', index);
  };

  const getItemIndex = (item: InvoiceItem): number => {
    const index = form.values.invoiceItems.findIndex(ii => ii.invoiceItemId === item.invoiceItemId);
    if (index === -1) {
      console.warn('InvoiceItem not found in form values:', item.invoiceItemId);
    }
    return index;
  };

  /**
   * The fields this edit moved, compared against what the invoice holds.
   *
   * Compared against the query data rather than Mantine's `form.isDirty`, because `initialValues`
   * above holds placeholder defaults and the `setValues` in the effect below does not move the
   * dirty baseline. A field that did not move must not travel to the package: an edit that only
   * changed the due date would otherwise rewrite the membership's auto-pay and card.
   */
  const getChangedApplyScopeFields = (payload: InvoiceUpdate): ApplyScopeField[] => {
    const invoice = invoiceQuery.data?.data;
    if (!invoice) {
      return [];
    }

    const changed: ApplyScopeField[] = [];
    if (payload.autoPay !== invoice.autoPay) {
      changed.push('auto_pay');
    }
    if (
      payload.paymentMethod !== (invoice.paymentMethod?.paymentMethodId ?? null) ||
      payload.nonStripePaymentMethod !== (invoice.nonStripePaymentMethod ?? null)
    ) {
      changed.push('payment_method');
    }
    return changed;
  };

  const saveInvoice = (
    payload: InvoiceUpdate,
    scope: ApplyScope | null,
    changedFields: ApplyScopeField[],
  ): void => {
    updateInvoiceMutation.mutate(
      { ...payload, applyToPackage: scope === 'package' },
      {
        onSuccess: response => {
          const propagation = response.data.packagePropagation;
          if (scope) {
            trackInvoiceApplyScopeSelected(
              payload.invoiceId,
              scope,
              changedFields,
              propagation?.chainPropagation?.packagesUpdated ?? 0,
              propagation?.refusalCode ?? null,
            );
          }
          successNotification(invoiceUpdateSuccessMessage(propagation, changedFields));
          setPendingEdit(null);
          setState('View');
        },
        onError: error => {
          errorNotification('Update invoice failed', error.response?.data.message);
        },
      },
    );
  };

  /**
   * Whether the picked day differs from the one the invoice holds.
   *
   * An explicit comparison against the hydrated value rather than `form.isDirty()`: `initialValues`
   * holds placeholder defaults and the `setValues` in the effect below does not move Mantine's
   * dirty baseline, so the library's answer is unreliable in this form.
   */
  const hydratedPaymentDate = invoiceQuery.data?.data.paymentDate
    ? parseDateTimeToDate(invoiceQuery.data.data.paymentDate, session?.gym?.timezone)
    : null;
  const pickedPaymentDate = form.values.paymentDate;
  const isPaymentDateChanged =
    pickedPaymentDate !== null &&
    (hydratedPaymentDate === null || !dayjs(pickedPaymentDate).isSame(dayjs(hydratedPaymentDate), 'day'));

  useEffect(() => {
    setIsPaymentDateDirty(state === 'Edit Payment Date' && isPaymentDateChanged);
  }, [state, isPaymentDateChanged, setIsPaymentDateDirty]);

  const savePaymentDate = (values: FormValues): void => {
    const invoice = invoiceQuery.data?.data;
    if (!values.paymentDate || !invoice) {
      return;
    }

    const previous = invoice.paymentDate
      ? parseDateTimeToDate(invoice.paymentDate, session?.gym?.timezone)
      : null;

    updateInvoicePaymentDateMutation.mutate(
      {
        invoiceId: values.invoiceId,
        paymentDate: dayjs(values.paymentDate).format('YYYY-MM-DD'),
      },
      {
        onSuccess: () => {
          if (previous) {
            trackInvoicePaymentDateEdited(
              values.invoiceId,
              invoice.nonStripePaymentMethod,
              dayjs(values.paymentDate).diff(dayjs(previous), 'day'),
              !dayjs(values.paymentDate).isSame(dayjs(previous), 'month'),
            );
          }
          successNotification('Payment date updated');
          setState('View');
        },
        onError: error => {
          errorNotification('Update payment date failed', error.response?.data.message);
        },
      },
    );
  };

  const handleSubmit = (values: FormValues): void => {
    const payload: InvoiceUpdate = {
      invoiceId: values.invoiceId,
      dueDate: dayjs(values.dueDate).format('YYYY-MM-DD'),
      autoPay: values.autoPay === 'True',
      paymentMethod: values.nonStripePaymentMethod ? null : values.paymentMethod,
      nonStripePaymentMethod: values.nonStripePaymentMethod,
      applyToPackage: false,
      invoiceItems: values.invoiceItems.map(ii => ({
        invoiceItemId: ii.invoiceItemId,
        productPrice: ii.productPrice,
        quantity: ii.quantity,
        itemPrice: Math.round(ii.itemPrice * 100),
        taxRateValue: ii.taxRateValue / 100,
        discount: ii.discount,
      })),
    };

    const changedFields = getChangedApplyScopeFields(payload);
    // A retail or fees invoice has no membership to apply to, so there is nothing to ask. The
    // backend treats the flag as a no-op for the same case, since the package can also end
    // between the drawer loading and Save. A staff member without the package permission is the
    // same shape: invoice-only is the only scope they have, so asking would offer them a 403.
    const hasPackage = Boolean(invoiceQuery.data?.data.athletePackage);
    if (changedFields.length === 0 || !hasPackage || !canMutateAthletePackages) {
      saveInvoice(payload, null, changedFields);
      return;
    }

    setPendingEdit({ payload, changedFields });
  };

  /**
   * The Payment Date path deliberately bypasses the invoice-edit validation rules.
   *
   * None of them is the staff member's to satisfy when the date is the only field they can
   * change, and one of them would block the save outright: a legacy invoice with no method on
   * record carries `Unknown`, which `_validate_payment_type` allows to keep auto-pay on, so the
   * autoPay rule fails and `form.onSubmit` never reaches its handler. Those are exactly the
   * invoices the spec puts in scope, and the failure is silent.
   */
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    if (state === 'Edit Payment Date') {
      event.preventDefault();
      savePaymentDate(form.values);
      return;
    }
    form.onSubmit(handleSubmit)(event);
  };

  useShallowEffect(() => {
    if (invoiceQuery.isSuccess && invoiceQuery.data?.data) {
      const { editEnabled } = getInvoiceActionEnabledMap(invoiceQuery.data.data);
      setIsEditEnabled(editEnabled);
      form.setValues({
        invoiceId: invoiceQuery.data.data.invoiceId,
        dueDate: parseDateTimeToDate(invoiceQuery.data.data.dueDate, session?.gym?.timezone),
        paymentDate: invoiceQuery.data.data.paymentDate
          ? parseDateTimeToDate(invoiceQuery.data.data.paymentDate, session?.gym?.timezone)
          : null,
        autoPay: invoiceQuery.data.data.autoPay ? 'True' : 'False',
        paymentMethod:
          invoiceQuery.data.data.paymentMethod?.paymentMethodId ||
          invoiceQuery.data.data?.nonStripePaymentMethod,
        nonStripePaymentMethod:
          (invoiceQuery.data.data?.nonStripePaymentMethod as NonStripePaymentMethod) || null,
        invoiceItems: invoiceQuery.data.data.invoiceItems.map(iv => ({
          invoiceItemId: iv.invoiceItemId,
          productName: iv.productName,
          productPrice: iv.productPrice,
          productId: iv.product,
          itemPrice: iv.itemPrice / 100,
          quantity: iv.quantity,
          taxRateValue: iv.taxRateValue * 100,
          productType: iv.productType,
          createdAt: iv.createdAt,
          discountAmount: iv.discountAmount ? iv.discountAmount / 100 : undefined,
          discount: iv.discount?.discountId ?? null,
          discountType: iv.discount?.valueType,
          discountValue: iv.discount ? getDiscountFormValue(iv.discount) : undefined,
          discountName: iv.discount?.name ?? undefined,
        })),
      });
    }
  }, [invoiceQuery.data?.data, invoiceQuery.isSuccess]);

  const renderInvoiceItem = (item: InvoiceItem, showDeleteIcon: boolean = false): JSX.Element | null => {
    const index = getItemIndex(item);

    if (index === -1) {
      return null;
    }

    const isDeletable = !isDisabled && showDeleteIcon && canDeleteItem(item);

    const discountSummary = (): Pick<DiscountList, 'valueType' | 'amount' | 'percentage'> => ({
      amount: (item.discountValue ?? 0) * 100,
      percentage: item.discountValue?.toString() ?? null,
      valueType: item.discountType ?? 'PERCENTAGE',
    });

    return (
      <Stack spacing='xs' key={item.invoiceItemId}>
        <div className={classes.invoices}>
          <TextInput
            {...form.getInputProps(`invoiceItems.${index}.productName`)}
            label='Product Name'
            disabled
          />
          <MoneyField
            country={country}
            {...form.getInputProps(`invoiceItems.${index}.itemPrice`)}
            label={copy.components.MoneyField.priceInputLabel}
            placeholder={copy.components.MoneyField.priceInputPlaceholder}
            min={0}
            required
            disabled={isDisabled}
          />
          <NumberInput
            {...form.getInputProps(`invoiceItems.${index}.quantity`)}
            label='Quantity'
            placeholder='Set quantity'
            min={1}
            step={1}
            disabled={isPackageItem(item) || isDisabled}
          />
          <TaxRatePercentageInput
            {...form.getInputProps(`invoiceItems.${index}.taxRateValue`)}
            label='Tax Rate'
            placeholder='Set tax rate'
            required
            disabled={isDisabled}
          />
          {isDeletable ? (
            <ActionIcon
              color='red'
              size='lg'
              onClick={() => handleDeleteItem(index)}
              mt='xl'
              aria-label={`Delete ${item.productName}`}
            >
              <IconTrash size='1.2rem' />
            </ActionIcon>
          ) : (
            <div />
          )}
        </div>

        {!isDisabled && (
          <AvailableDiscounts
            athleteId={invoiceQuery.data?.data.athlete.athleteId}
            productId={item.productId}
            selectedDiscountId={item.discount}
            form={form}
            fieldPath={`invoiceItems.${index}.discount`}
            oneLine
          />
        )}
        {isDisabled && item.discount && (
          <Group spacing='sm'>
            <Group align='center' spacing='xs'>
              <IconTag size='1.4rem' color='#4AC360' />
              <Text size='sm' color='green' weight={600}>
                Applied Discount: {item.discountName}
              </Text>
            </Group>
            <Text size='sm' color='green' align='end'>
              {formatDiscountValue(discountSummary(), formatCurrency, true)}
            </Text>
          </Group>
        )}
      </Stack>
    );
  };

  if (invoiceQuery.isError) {
    return (
      <KiloAlert title='Error' variantStyle='error' color='red'>
        Something went wrong while fetching data!
      </KiloAlert>
    );
  }

  const tapToPayCard =
    invoiceQuery.data?.data?.isTapToPay && invoiceQuery.data?.data.paymentMethod
      ? invoiceQuery.data?.data.paymentMethod
      : null;

  const invoice = invoiceQuery.data?.data;
  // The picker offers only the window the backend accepts, so the twelve-month message appears
  // only when a date is typed rather than picked.
  const paymentDateMinDate = invoice ? dayjs(invoice.createdAt).subtract(12, 'month').toDate() : undefined;
  const paymentDateMaxDate = dayjs().toDate();

  return (
    <form onSubmit={handleFormSubmit} id='invoice-form'>
      {invoiceQuery.isLoading && <LoadingOverlay visible />}
      <ApplyScopeModal
        opened={pendingEdit !== null}
        changedFields={pendingEdit?.changedFields ?? []}
        isSaving={updateInvoiceMutation.isLoading}
        onCancel={() => setPendingEdit(null)}
        onConfirm={scope =>
          pendingEdit && saveInvoice(pendingEdit.payload, scope, pendingEdit.changedFields)
        }
      />
      <Stack spacing='md' pb='lg'>
        <Group grow align='top'>
          <DatePickerInput
            {...form.getInputProps('dueDate')}
            label='Due Date'
            required
            disabled={isDisabled}
          />
          {invoice?.isPaymentDateVisible && (
            <Stack spacing={4}>
              <DatePickerInput
                {...form.getInputProps('paymentDate')}
                label='Payment Date'
                required
                disabled={isPaymentDateDisabled}
                minDate={paymentDateMinDate}
                maxDate={paymentDateMaxDate}
              />
              {!invoice.isPaymentDateEditable && (
                <Text size='xs' color='dimmed'>
                  Recorded when Stripe confirmed the payment. It cannot be changed.
                </Text>
              )}
            </Stack>
          )}
          <Group position='right' align='top'>
            {state === 'View' && (
              <Button
                size='sm'
                disabled={!isEditEnabled}
                onClick={() => {
                  // A paid invoice opens straight into the one field it can change; every other
                  // invoice opens the full edit form. The two are mutually exclusive, because
                  // isPaymentDateEditable is only ever true on PAID and PARTIALLY REFUNDED.
                  setState(invoice?.isPaymentDateEditable ? 'Edit Payment Date' : 'Edit Invoice');
                }}
              >
                Edit
              </Button>
            )}
          </Group>
        </Group>

        <div className={classes.pm}>
          <Stack spacing='xs'>
            <PaymentMethodSelect
              placeholder={tapToPayCard ? getPaymentMethodName(tapToPayCard) : 'Select option'}
              athleteId={invoiceQuery.data?.data.athlete.athleteId}
              disabled={isDisabled}
              label='Payment Methods'
              allowCash
              onChange={(value: string) => handlePaymentChange(value)}
              value={form.values.paymentMethod}
            />
            {invoiceQuery.data?.data.isTapToPay && (
              <Group spacing='xs' align='center'>
                <IconExclamationCircle size='1.2rem' color={theme.colors.gray[6]} />
                <Text size='xs' color='dimmed'>
                  This invoice was collected via Tap to Pay
                </Text>
              </Group>
            )}
          </Stack>
          <Stack spacing='xs'>
            <Select
              {...form.getInputProps('autoPay')}
              label='AutoPay'
              required
              data={[
                { value: 'True', label: 'On' },
                { value: 'False', label: 'Off' },
              ]}
              disabled={isDisabled || form.values.nonStripePaymentMethod !== null}
              sx={{ width: isDesktop ? '100%' : '30%' }}
            />
            {form.values.autoPay === 'True' && form.values.dueDate < new Date() && (
              <Text size='xs' color='dimmed' pl='sm'>
                Autopay will not be attempted
                <br /> for invoices due in the past
              </Text>
            )}
          </Stack>
        </div>

        {/* Package Items */}
        {groupedInvoiceItems.packageItems.map(item => renderInvoiceItem(item, false))}

        {groupedInvoiceItems.hasBothTypes && <Divider my='sm' />}

        {/* Retail/Fees Items Section - ordered by createdAt date */}
        {groupedInvoiceItems.retailFeesItems.map(item => (
          <div key={item.invoiceItemId}>
            {item.createdAt && (
              <Text size='sm' color='black' fw={600} mb='xs'>
                {formatDateWithOrdinal(item.createdAt)}
              </Text>
            )}
            {renderInvoiceItem(item, true)}
          </div>
        ))}

        <InvoiceSummary
          athleteName={invoiceQuery.data?.data.athlete.fullName}
          invoiceItems={form.values.invoiceItems}
          isViewing={state === 'View'}
          invoice={invoiceQuery.data?.data}
        />
      </Stack>
    </form>
  );
};

export default InvoiceInfoTab;
